import React, { useState, useEffect } from 'react';
import { PageData, TOTAL_PAGES } from '../../types/tome';
import { ReaderSettings } from '../../types/reader';
import { Bookmark as BookmarkIcon, Clock, User, Terminal } from 'lucide-react';
import { ArtworkCard } from '../Artwork/ArtworkCard';
import { getNarrationController, loadAIConfig } from '../../services/googleGenAI';
import { isTextBeingSpoken, formatPageParagraphs, FormattedParagraph } from '../../utils/proseFormatter';
import { CHARACTER_PORTRAITS } from '../../services/visualRegistry';

export interface PageSlotProps {
  pageData: PageData;
  settings: ReaderSettings;
  isBookmarked?: boolean;
  onToggleBookmark?: () => void;
}

export const PageSlot: React.FC<PageSlotProps> = ({
  pageData,
  settings,
  isBookmarked,
  onToggleBookmark,
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingText, setSpeakingText] = useState('');
  const [highlightEnabled, setHighlightEnabled] = useState(() => {
    try {
      return loadAIConfig().enableReadAlongHighlight !== false;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      const narration = getNarrationController();
      const unsub = narration.subscribe((speaking, _paused, currentText) => {
        setIsSpeaking(speaking);
        setSpeakingText(currentText || '');
        setHighlightEnabled(loadAIConfig().enableReadAlongHighlight !== false);
      });
      return unsub;
    } catch {
      // Narration controller safe fallback
    }
  }, []);
  const {
    pageNumber,
    actNumber,
    actTitle,
    chapterTitle,
    contentArchetype,
    speaker,
    timestamp,
    title,
    text,
    shader,
    audio,
    artwork,
  } = pageData;

  // Determine typeface & sizing classes
  const fontClass =
    settings.fontFamily === 'mono' || settings.fontFace === 'mono'
      ? 'font-mono'
      : settings.fontFamily === 'sans' || settings.fontFace === 'sans'
      ? 'font-sans'
      : 'font-serif';

  const sizeClass = `typography-${settings.fontSize}`;
  const spacingClass = `spacing-${settings.lineSpacing}`;

  return (
    <article
      className={`page-card archetype-${contentArchetype} ${fontClass} ${sizeClass} ${spacingClass}`}
      data-page-number={pageNumber}
      aria-label={`Page ${pageNumber} of ${TOTAL_PAGES}`}
    >
      {/* Top Meta Bar */}
      <header className="page-header">
        <div className="page-header-meta">
          <div className="page-act-badge">
            <span className="act-tag">
              {actNumber === 0
                ? 'FRONT MATTER'
                : actNumber === 11
                ? 'APPENDIX'
                : `ACT ${actNumber}`}
            </span>
            <span className="act-divider">/</span>
            <span className="act-name">{actTitle}</span>
          </div>
          <div className="page-header-right">
            <span className={`archetype-pill archetype-${contentArchetype}`}>
              {contentArchetype.toUpperCase()}
            </span>
            {onToggleBookmark && (
              <button
                className={`bookmark-toggle-btn ${isBookmarked ? 'bookmarked' : ''}`}
                onClick={onToggleBookmark}
                title={isBookmarked ? 'Remove Bookmark' : 'Bookmark this page'}
                aria-label={isBookmarked ? 'Remove Bookmark' : 'Bookmark this page'}
              >
                <BookmarkIcon size={16} fill={isBookmarked ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        </div>

        <div className="page-title-group">
          <h1 className="page-title">{title}</h1>
          <h2 className="page-chapter-sub">{chapterTitle}</h2>
        </div>

        {(speaker || timestamp) && (
          <div className="page-telemetry-strip">
            {timestamp && (
              <span className="telemetry-item">
                <Clock size={13} className="telemetry-icon" />
                <span className="telemetry-text">{timestamp}</span>
              </span>
            )}
            {speaker && (
              <span className="telemetry-item">
                <User size={13} className="telemetry-icon" />
                <span className="telemetry-text">{speaker}</span>
              </span>
            )}
            <span className="telemetry-item telemetry-mode">
              <Terminal size={13} className="telemetry-icon" />
              <span className="telemetry-text">
                {shader.theme.toUpperCase()} // {audio.mood.toUpperCase()}
              </span>
            </span>
          </div>
        )}
      </header>

      {/* Embedded Atmospheric Semi-Animated Artwork */}
      {artwork && (
        <ArtworkCard
          artwork={artwork}
          pageNumber={pageNumber}
          textParagraphs={text}
          theme={settings.theme}
          speaker={speaker}
        />
      )}

      {/* Main Narrative Body */}
      <div className="page-prose-body">
        {formatPageParagraphs(text, speaker).map((para: FormattedParagraph, idx: number) => {
          const isBeingReadAloud =
            isSpeaking &&
            highlightEnabled &&
            isTextBeingSpoken(speakingText, para.text);

          if (para.type === 'dialogue') {
            const charPortrait =
              (para.characterId && CHARACTER_PORTRAITS[para.characterId as keyof typeof CHARACTER_PORTRAITS]) ||
              '/art/marcus_ward_portrait.jpg';

            return (
              <div
                key={idx}
                className={`inline-dialogue-dossier ${isBeingReadAloud ? 'reading-aloud-active dialogue-reading-active' : ''}`}
                style={{ borderLeftColor: para.accentColor || '#00f3ff' }}
              >
                <div className="dialogue-avatar-dossier-col">
                  <div
                    className="dialogue-hex-frame"
                    style={{ borderColor: para.accentColor || '#00f3ff' }}
                  >
                    <img
                      src={charPortrait}
                      alt={para.speaker || 'Character'}
                      className="dialogue-avatar-img"
                    />
                  </div>
                  <span
                    className={`dialogue-biometric-dot ${isBeingReadAloud ? 'biometric-dot-pulse' : ''}`}
                    style={{ background: para.accentColor || '#00f3ff' }}
                  />
                </div>

                <div className="dialogue-body-col">
                  <div className="dialogue-header">
                    <span
                      className="dialogue-speaker-name"
                      style={{ color: para.accentColor || '#00f3ff' }}
                    >
                      {para.speaker?.toUpperCase()}
                    </span>
                    {para.speakerRole && (
                      <span className="dialogue-speaker-role">
                        // {para.speakerRole}
                      </span>
                    )}
                    {para.emotion && (
                      <span
                        className="dialogue-emotion-badge"
                        style={{
                          color: para.emotionColor || '#ff4081',
                          borderColor: `${para.emotionColor || '#ff4081'}55`,
                          backgroundColor: `${para.emotionColor || '#ff4081'}15`,
                        }}
                        title={`Biometric Vocal State: ${para.emotion}`}
                      >
                        <span
                          className="emotion-pulse-dot"
                          style={{ backgroundColor: para.emotionColor || '#ff4081' }}
                        />
                        <span className="emotion-tag-text">{para.emotion}</span>
                      </span>
                    )}
                    {isBeingReadAloud && (
                      <span className="reading-telemetry-tag">TRANSMITTING</span>
                    )}
                  </div>
                  <p className="dialogue-speech-text">“{para.text}”</p>
                </div>
              </div>
            );
          }

          if (para.type === 'marginalia') {
            return (
              <div
                key={idx}
                className={`inline-marginalia-cipher ${isBeingReadAloud ? 'reading-aloud-active' : ''}`}
              >
                <div className="marginalia-header">
                  <span className="marginalia-tag">{para.speaker || 'SYSTEM RECORD'}</span>
                  <span className="marginalia-divider">//</span>
                  <span className="marginalia-sub">COORDINATE TIMESTAMP {timestamp || '00:00.00'}</span>
                  {isBeingReadAloud && (
                    <span className="reading-telemetry-tag">ACTIVE READOUT</span>
                  )}
                </div>
                <p className="marginalia-body">{para.text}</p>
              </div>
            );
          }

          return (
            <p
              key={idx}
              className={`prose-paragraph ${
                contentArchetype === 'log' || contentArchetype === 'ledger'
                  ? 'font-mono'
                  : ''
              } ${isBeingReadAloud ? 'reading-aloud-active' : ''}`}
            >
              {para.text}
              {isBeingReadAloud && (
                <span className="reading-telemetry-tag">VOX</span>
              )}
            </p>
          );
        })}
      </div>

      {/* Page Card Footer */}
      <footer className="page-footer">
        <div className="page-footer-left">
          <span className="page-coord-hash">
            0x{(pageNumber * 0x1f3c).toString(16).toUpperCase().padStart(8, '0')}
          </span>
          <span className="page-stasis-indicator">● STASIS NOMINAL</span>
        </div>
        <div className="page-footer-right">
          <span className="page-number-display">PAGE {pageNumber} / {TOTAL_PAGES}</span>
        </div>
      </footer>
    </article>
  );
};
